import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Chip, IconButton, TextField, CircularProgress, Typography, alpha } from '@mui/material';
import { getChatbotContext } from '../utils/chatbotContext';
import { getRouteSuggestions } from '../utils/chatbotSuggestions';
import { DEFAULT_QUICK_ACTIONS } from '../constants/quickActions';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  FilePresent as FilePresentIcon,
  MicOutlined as MicIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

interface Suggestion {
  label: string;
  query: string;
  intent: string | null;
}

interface ChatInputProps {
  activeSuggestions: Suggestion[];
  attachedFiles: File[];
  inputValue: string;
  setInputValue: (value: string) => void;
  isLoading: boolean;
  handleSuggestionClick: (query: string, intent?: string | null) => void;
  handleSendMessage: () => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: (fileName: string) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  inputRef: React.RefObject<HTMLInputElement>;
  activeIntent: string | null;
  missingFields: string[];
  isAwaitingIntentConfirmation: boolean;
  chatbotExtractionLoading: boolean;
}

export const ChatInput: React.FC<ChatInputProps> = React.memo(
  ({
    activeSuggestions,
    attachedFiles,
    inputValue,
    setInputValue,
    isLoading,
    handleSuggestionClick,
    handleSendMessage,
    handleFileSelect,
    removeFile,
    fileInputRef,
    inputRef,
    activeIntent: _activeIntent,
    missingFields: _missingFields,
    isAwaitingIntentConfirmation,
    chatbotExtractionLoading,
  }) => {
    const isInputDisabled = isLoading || isAwaitingIntentConfirmation;
    const location = useLocation();
    const context = getChatbotContext(location);
    const pageSuggestions = getRouteSuggestions(location.pathname, location.search, context);

    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef<any>(null);

    useEffect(() => {
      return () => {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
      };
    }, []);

    const toggleListening = () => {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (!SpeechRecognition) {
        alert('Speech recognition is not supported in this browser. Please try Chrome, Edge, or Safari.');
        return;
      }

      if (isListening) {
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        setIsListening(false);
      } else {
        try {
          const recognition = new SpeechRecognition();
          recognition.continuous = false;
          recognition.interimResults = false;
          recognition.lang = 'en-US';

          recognition.onstart = () => {
            setIsListening(true);
          };

          recognition.onerror = (event: any) => {
            console.error('Speech recognition error:', event.error);
            setIsListening(false);
          };

          recognition.onend = () => {
            setIsListening(false);
          };

          recognition.onresult = (event: any) => {
            const transcript = event.results[0][0].transcript;
            if (transcript) {
              setInputValue(inputValue ? `${inputValue} ${transcript}` : transcript);
            }
          };

          recognitionRef.current = recognition;
          recognition.start();
        } catch (err) {
          console.error('Failed to start speech recognition:', err);
          setIsListening(false);
        }
      }
    };
    return (
      <Box>
        {/* ATTACHED FILES PREVIEW ROW */}
        {attachedFiles.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              padding: '4px 16px 8px 16px',
              flexWrap: 'wrap',
              backgroundColor: '#FFFFFF',
            }}
          >
            {attachedFiles.map((file, idx) => (
              <Chip
                key={idx}
                icon={<FilePresentIcon sx={{ fontSize: 16 }} />}
                label={`${file.name.substring(0, 15)}${file.name.length > 15 ? '...' : ''}`}
                size="small"
                onDelete={() => removeFile(file.name)}
                variant="outlined"
                sx={{
                  fontSize: '11px',
                  backgroundColor: '#F5F5F7',
                  borderColor: '#E0E0E0',
                  '& .MuiChip-deleteIcon': {
                    fontSize: '14px',
                    color: '#757575',
                    '&:hover': { color: '#111111' },
                  },
                }}
              />
            ))}
          </Box>
        )}

        {/* QUICK SUGGESTIONS & ACTIONS LIST */}
        {!isAwaitingIntentConfirmation && (
          <Box
            className="ai-suggestions-list"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              padding: '12px 16px',
              borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              backgroundColor: '#F5F5F7',
              maxHeight: '200px',
              overflowY: 'auto',
              '&::-webkit-scrollbar': {
                width: '4px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0, 0, 0, 0.12)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: 'rgba(0, 0, 0, 0.24)',
              },
            }}
          >
            {activeSuggestions.length > 0 ? (
              // Specific Active Suggestions (when returned by intent/backend)
              activeSuggestions.map((sug, idx) => (
                <Box
                  key={idx}
                  component="button"
                  onClick={() => handleSuggestionClick(sug.label, sug.intent)}
                  className="ai-suggestion-btn"
                  sx={{
                    width: '100%',
                    background: '#FFFFFF',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: '10px',
                    padding: '12px 16px',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    color: '#1A1A1A',
                    boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.02)',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <span>{sug.label}</span>
                  <ChevronRightIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                </Box>
              ))
            ) : (
              // Default Quick Actions and Route suggestions shown together
              <>
                {/* PAGE SUGGESTIONS GROUP */}
                {pageSuggestions && pageSuggestions.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: '#757575',
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        mb: 0.5,
                      }}
                    >
                      For This Page
                    </Typography>
                    {pageSuggestions.map((sug, idx) => (
                      <Box
                        key={idx}
                        component="button"
                        onClick={() => handleSuggestionClick(sug.query, sug.intent)}
                        className="ai-suggestion-btn"
                        sx={{
                          width: '100%',
                          background: '#FFFFFF',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          borderRadius: '10px',
                          padding: '12px 16px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 500,
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          color: '#424242',
                          boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.02)',
                          '&:hover': {
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <span>{sug.label}</span>
                        <ChevronRightIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* QUICK ACTIONS GROUP */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    mt: pageSuggestions && pageSuggestions.length > 0 ? 0.5 : 0,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: '#757575',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      mb: 0.5,
                    }}
                  >
                    Quick Actions
                  </Typography>
                  {DEFAULT_QUICK_ACTIONS.map((action, idx) => (
                    <Box
                      key={idx}
                      component="button"
                      onClick={() => handleSuggestionClick(action.query, action.intent)}
                      className="ai-suggestion-btn"
                      sx={{
                        width: '100%',
                        background: '#FFFFFF',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        borderRadius: '10px',
                        padding: '12px 16px',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: 500,
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#1A1A1A',
                        boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.02)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          backgroundColor: theme => alpha(theme.palette.primary.main, 0.04),
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {action.icon &&
                          React.createElement(action.icon, { sx: { fontSize: 16, color: 'primary.main' } })}
                        <span>{action.label}</span>
                      </Box>
                      <ChevronRightIcon sx={{ fontSize: 16, opacity: 0.7 }} />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}

        {/* INPUT AREA */}
        <Box
          className="ai-input-area"
          sx={{
            padding: '12px 16px',
            backgroundColor: '#FFFFFF',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '8px',
              padding: '4px 8px',
              backgroundColor: '#F5F5F7',
              transition: 'all 0.25s ease',
              '&:focus-within': {
                borderColor: 'primary.main',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
              },
            }}
          >
            {/* Attachment Button */}
            <IconButton
              size="small"
              component="label"
              disabled={isInputDisabled || chatbotExtractionLoading}
              aria-label="Attach file to chat"
              sx={{
                color: '#757575',
                mr: 0.5,
                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
              }}
            >
              {chatbotExtractionLoading ? (
                <CircularProgress size={20} sx={{ color: '#757575' }} />
              ) : (
                <AttachFileIcon sx={{ fontSize: 20 }} />
              )}
              <input
                ref={fileInputRef}
                type="file"
                hidden
                multiple
                onChange={handleFileSelect}
                accept=".pdf,.doc,.docx,.xlsx,.csv,.jpg,.png"
              />
            </IconButton>

            {/* Text Input */}
            <TextField
              inputRef={inputRef}
              fullWidth
              size="small"
              placeholder="Ask anything about billing, routes, SLA..."
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              multiline
              minRows={1}
              maxRows={6}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!isInputDisabled) {
                    handleSendMessage();
                  }
                }
              }}
              disabled={isInputDisabled}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                'aria-label': 'Ask anything about billing, routes, SLA',
              }}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: '13px',
                  color: '#1A1A1A',
                  padding: '4px 0',
                  lineHeight: 1.4,
                },
              }}
            />

            {/* Microphone Button */}
            <IconButton
              size="small"
              disabled={isInputDisabled}
              onClick={toggleListening}
              aria-label={isListening ? 'Stop voice input' : 'Use voice input'}
              sx={{
                color: isListening ? 'error.main' : '#757575',
                mx: 0.5,
                animation: isListening ? 'geminiPulse 1.2s infinite' : 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  color: isListening ? 'error.dark' : 'primary.main',
                },
              }}
            >
              <MicIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>

          {/* Send Button */}
          <IconButton
            onClick={() => handleSendMessage()}
            disabled={isInputDisabled || !inputValue.trim()}
            aria-label="Send message"
            sx={{
              color: 'white',
              backgroundColor: 'primary.main',
              width: 38,
              height: 38,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
                color: '#9E9E9E',
              },
            }}
          >
            <SendIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Box>
    );
  },
);

ChatInput.displayName = 'ChatInput';
